import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { AppRoute } from 'app/app.route.const';
import { AccountRight } from 'logic/api-models/api-models';
import { Breakpoints } from 'logic/hooks/use-breakpoints';
import { getAccountRights } from 'logic/store/authentication/authentication.selectors';
import { conferenceSlice } from 'logic/store/conference/conference.slice';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { useInProgress } from 'logic/store/tracker/tracker.hooks';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { colors } from 'styles/colors';

const HELP_LINKTREE_URL = 'https://linktr.ee/tutorspace';

/**
 * `stacked` - one hint below the other (sidebar)
 * `inline` - hints side by side from tablet upwards (slim bar below the content on mobile)
 */
export type SupportHintsLayout = 'stacked' | 'inline';

type SupportHintAction = {
  label: string;
  /** external target, rendered as a link that opens in a new tab */
  href?: string;
  onClick?: () => void;
  /** action is running, render the label as plain text */
  pending?: boolean;
};

type SupportHint = {
  headline: string;
  actions: SupportHintAction[];
};

/** The support hints are only relevant for users who have access to the classroom. */
export const useHasSupportHints = (): boolean => {
  const accountRights = useSelector(getAccountRights);

  return !!accountRights?.some(
    (accountRight) =>
      accountRight === AccountRight.ELITESEEKER || accountRight === AccountRight.ELITETUROR
  );
};

const HintList = styled.div<{ layout: SupportHintsLayout }>`
  display: grid;
  grid-gap: 16px;
  align-items: start;

  ${({ layout }) =>
    layout === 'inline' &&
    css`
      @media (${Breakpoints.minTablet}) {
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 24px;
      }
    `}
`;

const Headline = styled.p`
  margin: 0;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: baseline;
`;

const Pointer = styled.span`
  margin-right: 4px;
  flex-shrink: 0;
`;

const actionStyles = css`
  text-decoration: underline;
  text-align: left;
`;

const ActionLink = styled.a`
  ${actionStyles};
`;

const ActionButton = styled.button`
  ${actionStyles};
  padding: 0;
  border: none;
  background: none;
  color: ${colors.primary};
  cursor: pointer;
`;

const PendingAction = styled.span`
  color: ${colors.grayText};
`;

const HintAction: FC<{ action: SupportHintAction }> = ({ action }) => (
  <ActionRow>
    <Pointer aria-hidden="true">👉</Pointer>
    {action.pending ? (
      <PendingAction>{action.label}</PendingAction>
    ) : action.href ? (
      <ActionLink href={action.href} target="_blank" rel="noreferrer">
        {action.label}
      </ActionLink>
    ) : (
      <ActionButton type="button" onClick={action.onClick}>
        {action.label}
      </ActionButton>
    )}
  </ActionRow>
);

export const SupportHints: FC<{ layout?: SupportHintsLayout; className?: string }> = ({
  layout = 'stacked',
  className,
}) => {
  const dispatch = useDispatch();
  const isTestCallLoading = useInProgress(conferenceSlice.actions.createAndJoinTestCall);

  const hints: SupportHint[] = [
    {
      headline: translate(translationKeys.sidebar.custom.testCall.doYouWantToTest),
      actions: [
        isTestCallLoading
          ? { label: translate(translationKeys.sidebar.custom.testCall.loading), pending: true }
          : {
              label: translate(translationKeys.sidebar.custom.testCall.start),
              onClick: () => dispatch(conferenceSlice.actions.createAndJoinTestCall()),
            },
      ],
    },
    {
      headline: translate(translationKeys.sidebar.custom.testCall.technicalIssues),
      actions: [
        {
          label: translate(translationKeys.sidebar.custom.testCall.bookSpecialist),
          href: AppRoute.External_CalendlyTechCheck,
        },
      ],
    },
    {
      headline: translate(translationKeys.sidebar.custom.help.headline),
      actions: [
        {
          label: translate(translationKeys.sidebar.custom.help.textForTutor),
          href: HELP_LINKTREE_URL,
        },
        {
          label: translate(translationKeys.sidebar.custom.help.textForSeeker),
          onClick: () =>
            dispatch(
              navigationSlice.actions.navigateTo({
                path: AppRoute.SupportAndHelpYouAskWeAnswer,
              })
            ),
        },
      ],
    },
  ];

  return (
    <HintList layout={layout} className={className}>
      {hints.map((hint) => (
        <div key={hint.headline}>
          <Headline>{hint.headline}</Headline>
          {hint.actions.map((action) => (
            <HintAction key={action.label} action={action} />
          ))}
        </div>
      ))}
    </HintList>
  );
};
